import React, { useState, useEffect } from 'react';
import { Typography, FlexGrid, Notification } from '@telus-uds/components-web';
import {
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';

import { reportsAPI, exportReportExcel } from '../utils/apiService';
import { formatNumber, getDefaultDateRange } from '../utils/formatters';
import SummaryCard from '../components/SummaryCard';
import FilterPanel from '../components/FilterPanel';
import LoadingSpinner from '../components/LoadingSpinner';
import EmptyState from '../components/EmptyState';
import ScopedDataNotice from '../components/ScopedDataNotice';
import DrillDownModal from '../components/DrillDownModal';
import { chartCardStyle, CHART_COLORS as COLORS } from '../utils/chartStyles';

const ORDER_TYPES = [
  { value: '', label: 'All' },
  { value: 'WLS', label: 'WLS' },
  { value: 'FM', label: 'Fully Managed' },
  { value: 'SOW', label: 'Statement of Work' },
  { value: 'VUL', label: 'VUL' },
  { value: 'OCDR', label: 'OCDR' },
  { value: 'CS', label: 'Cyber Security' }
];

const ORDER_STATUSES = [
  { value: '', label: 'All' },
  { value: 'INCOMPLETE', label: 'Incomplete' },
  { value: 'PENDING_VALIDATION', label: 'Pending Validation' },
  { value: 'ORDER_VALIDATED', label: 'Order Validated' },
  { value: 'SUBMITTED', label: 'Submitted' },
  { value: 'IN_PROGRESS', label: 'In Progress' },
  { value: 'ACTION_REQUIRED', label: 'Action Required' },
  { value: 'CONTRACT_SENT', label: 'Contract Sent' },
  { value: 'CONTRACT_SIGNED', label: 'Contract Signed' },
  { value: 'COMPLETED', label: 'Completed' },
  { value: 'ORDER_CANCELED', label: 'Order Canceled' },
  { value: 'WAITING_ON_SALES', label: 'Waiting on Sales' },
  { value: 'WAITING_ON_CREDIT', label: 'Waiting on Credit' },
  { value: 'WAITING_ON_CUSTOMER_RESPONSE', label: 'Waiting on Customer Response' }
];

const PROVINCES = [
  { value: '', label: 'All' },
  { value: 'AB', label: 'Alberta' },
  { value: 'BC', label: 'British Columbia' },
  { value: 'MB', label: 'Manitoba' },
  { value: 'NB', label: 'New Brunswick' },
  { value: 'NL', label: 'Newfoundland and Labrador' },
  { value: 'NT', label: 'Northwest Territories' },
  { value: 'NS', label: 'Nova Scotia' },
  { value: 'NU', label: 'Nunavut' },
  { value: 'ON', label: 'Ontario' },
  { value: 'PE', label: 'Prince Edward Island' },
  { value: 'QC', label: 'Quebec' },
  { value: 'SK', label: 'Saskatchewan' },
  { value: 'YT', label: 'Yukon Territory' }
];

const OrderActivityReport = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [isExporting, setIsExporting] = useState(false);
  const [drillDownType, setDrillDownType] = useState(null);
  const [filterValues, setFilterValues] = useState({
    startDate: getDefaultDateRange().startDate,
    endDate: getDefaultDateRange().endDate,
    orderType: '',
    status: '',
    province: ''
  });
  const [appliedFilters, setAppliedFilters] = useState(null);

  const fetchData = async (customFilters = null) => {
    setLoading(true);
    setError(null);
    try {
      const filters = customFilters || filterValues;
      setAppliedFilters(filters);
      const result = await reportsAPI.getOrderActivity(filters);
      setData(result.data);
    } catch (err) {
      setError(err.message || 'Failed to fetch order activity data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleFilterChange = (name, value) => {
    setFilterValues(prev => ({ ...prev, [name]: value }));
  };

  const handleApplyFilters = () => {
    fetchData();
  };

  const handleResetFilters = () => {
    const defaultFilters = {
      startDate: getDefaultDateRange().startDate,
      endDate: getDefaultDateRange().endDate,
      orderType: '',
      status: '',
      province: ''
    };
    setFilterValues(defaultFilters);
    // Fetch data immediately with default filters
    fetchData(defaultFilters);
  };

  const handleExportExcel = async () => {
    setIsExporting(true);
    try {
      const exportFilters = appliedFilters || filterValues;
      const fileName = `order-activity-report-${exportFilters.startDate}-to-${exportFilters.endDate}.xlsx`;
      await exportReportExcel('/reports/order-activity/export/excel', exportFilters, fileName);
    } catch (err) {
      alert('Failed to export Excel file. Please try again.');
    } finally {
      setIsExporting(false);
    }
  };

  const filters = [
    {
      name: 'startDate',
      label: 'Start Date',
      type: 'date',
      value: filterValues.startDate,
      placeholder: 'YYYY-MM-DD'
    },
    {
      name: 'endDate',
      label: 'End Date',
      type: 'date',
      value: filterValues.endDate,
      placeholder: 'YYYY-MM-DD'
    },
    {
      name: 'orderType',
      label: 'Order Type',
      type: 'select',
      value: filterValues.orderType,
      options: ORDER_TYPES
    },
    {
      name: 'status',
      label: 'Status',
      type: 'select',
      value: filterValues.status,
      options: ORDER_STATUSES
    },
    {
      name: 'province',
      label: 'Province',
      type: 'select',
      value: filterValues.province,
      options: PROVINCES
    }
  ];

  if (loading) {
    return (
      <div>
        <FilterPanel filters={filters} onFilterChange={handleFilterChange} onApply={handleApplyFilters} onReset={handleResetFilters} />
        <LoadingSpinner text="Loading Order Activity Report..." />
      </div>
    );
  }

  if (error && !data) {
    return (
      <div>
        <FilterPanel filters={filters} onFilterChange={handleFilterChange} onApply={handleApplyFilters} onReset={handleResetFilters} />
        <Notification variant={{ validation: 'error' }}>
          <div>
            <strong>Error:</strong> {error}
          </div>
        </Notification>
      </div>
    );
  }

  if (!data) {
    return (
      <div>
        <FilterPanel filters={filters} onFilterChange={handleFilterChange} onApply={handleApplyFilters} onReset={handleResetFilters} />
        <EmptyState title="No Data Available" />
      </div>
    );
  }

  const {
    total_orders, by_type, by_status, by_province, daily_distribution
  } = data;

  const orderTypeData = (by_type || []).map(item => ({
    name: item.label || 'Unknown',
    value: parseInt(item.count, 10)
  }));

  const statusData = (by_status || []).map(item => ({
    name: item.label || 'Unknown',
    count: parseInt(item.count, 10)
  }));

  const provinceData = (by_province || []).map(item => ({
    name: item.label || 'Unknown',
    value: parseInt(item.count, 10)
  }));

  const dailyData = (daily_distribution || [])
    .slice()
    .reverse()
    .map(item => ({
      date: item.date ? new Date(item.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : 'N/A',
      count: parseInt(item.count, 10)
    }));

  const topType = orderTypeData.slice().sort((a, b) => b.value - a.value)[0];
  const topProvince = provinceData.slice().sort((a, b) => b.value - a.value)[0];

  return (
    <div>
      <FilterPanel
        filters={filters}
        onFilterChange={handleFilterChange}
        onApply={handleApplyFilters}
        onReset={handleResetFilters}
        onExport={handleExportExcel}
        isExporting={isExporting}
      />
      <ScopedDataNotice />

      {/* Summary Cards */}
      <FlexGrid>
        <FlexGrid.Row>
          <FlexGrid.Col xs={12} md={6} lg={4} style={{ padding: '0 0.75rem', marginBottom: '1.5rem' }}>
            <SummaryCard
              title="Total Orders"
              value={formatNumber(total_orders)}
              icon="📦"
            />
          </FlexGrid.Col>
          <FlexGrid.Col xs={12} md={6} lg={4} style={{ padding: '0 0.75rem', marginBottom: '1.5rem' }}>
            <SummaryCard
              title="Top Order Type"
              value={(topType && topType.name) || 'N/A'}
              subText={topType ? `${formatNumber(topType.value)} orders` : undefined}
              icon="🏆"
            />
          </FlexGrid.Col>
          <FlexGrid.Col xs={12} md={6} lg={4} style={{ padding: '0 0.75rem', marginBottom: '1.5rem' }}>
            <SummaryCard
              title="Top Province"
              value={(topProvince && topProvince.name) || 'N/A'}
              subText={topProvince ? `${formatNumber(topProvince.value)} orders` : undefined}
              icon="📍"
            />
          </FlexGrid.Col>
        </FlexGrid.Row>
      </FlexGrid>

      {/* Charts Section */}
      <FlexGrid style={{ marginTop: '2rem' }}>
        <FlexGrid.Row>
          <FlexGrid.Col xs={12} md={6} style={{ padding: '0 0.75rem', marginBottom: '2rem' }}>
            <div
              style={chartCardStyle}
            >
              <Typography heading="h3" variant={{ size: 'h3' }} block style={{ marginBottom: '1rem', color: '#4b286d' }}>
                Orders by Type
              </Typography>
              <p style={{ fontSize: '0.75rem', color: '#999999', marginBottom: '0.5rem' }}>Click a slice to view the underlying orders</p>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={orderTypeData}
                    cx="38%"
                    cy="50%"
                    labelLine={false}
                    outerRadius={90}
                    fill="#8884d8"
                    dataKey="value"
                    onClick={entry => setDrillDownType((entry && entry.name) || (entry && entry.payload && entry.payload.name))}
                    style={{ cursor: 'pointer' }}
                  >
                    {orderTypeData.map((entry, index) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={COLORS[index % COLORS.length]}
                      />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value) => formatNumber(value)} />
                  <Legend
                    layout="vertical"
                    align="right"
                    verticalAlign="middle"
                    formatter={(value, entry) => `${value}: ${formatNumber(entry.payload.value)}`}
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </FlexGrid.Col>

          <FlexGrid.Col xs={12} md={6} style={{ padding: '0 0.75rem', marginBottom: '2rem' }}>
            <div
              style={chartCardStyle}
            >
              <Typography heading="h3" variant={{ size: 'h3' }} block style={{ marginBottom: '1rem', color: '#4b286d' }}>
                Orders by Status
              </Typography>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={statusData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" angle={-20} textAnchor="end" height={80} interval={0} tick={{ fontSize: 10 }} />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="count" fill="#0066CC" name="Order Count" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </FlexGrid.Col>
        </FlexGrid.Row>

        <FlexGrid.Row>
          <FlexGrid.Col xs={12} md={6} style={{ padding: '0 0.75rem', marginBottom: '2rem' }}>
            <div
              style={chartCardStyle}
            >
              <Typography heading="h3" variant={{ size: 'h3' }} block style={{ marginBottom: '1rem', color: '#4b286d' }}>
                Orders by Province
              </Typography>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={provinceData}
                    cx="38%"
                    cy="50%"
                    labelLine={false}
                    outerRadius={90}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {provinceData.map((entry, index) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={COLORS[index % COLORS.length]}
                      />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value) => formatNumber(value)} />
                  <Legend
                    layout="vertical"
                    align="right"
                    verticalAlign="middle"
                    formatter={(value, entry) => `${value}: ${formatNumber(entry.payload.value)}`}
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </FlexGrid.Col>

          <FlexGrid.Col xs={12} md={6} style={{ padding: '0 0.75rem', marginBottom: '2rem' }}>
            <div
              style={chartCardStyle}
            >
              <Typography heading="h3" variant={{ size: 'h3' }} block style={{ marginBottom: '1rem', color: '#4b286d' }}>
                Daily Order Volume
              </Typography>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={dailyData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Line
                    type="monotone"
                    dataKey="count"
                    stroke="#4b286d"
                    strokeWidth={2}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </FlexGrid.Col>
        </FlexGrid.Row>
      </FlexGrid>

      <DrillDownModal
        open={!!drillDownType}
        onClose={() => setDrillDownType(null)}
        title={`Orders — ${drillDownType}`}
        queryParams={{
          startDate: filterValues.startDate,
          endDate: filterValues.endDate,
          orderType: drillDownType,
          status: filterValues.status,
          province: filterValues.province
        }}
        fetchRecords={reportsAPI.getOrderActivityRecords}
        columns={[
          { key: 'order_id', label: 'Order ID' },
          { key: 'type_of_order', label: 'Type' },
          { key: 'status', label: 'Status' },
          { key: 'province', label: 'Province' },
          { key: 'legal_business_name', label: 'Business Name' },
          { key: 'created_at', label: 'Created At' }
        ]}
      />
    </div>
  );
};

export default OrderActivityReport;
